import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setModal, selectModal } from '../../../redux/modalSlice'
import {
  selectDropdowns,
  selectDropdownOptions,
} from '../../../redux/dropdownSlice'
import { useForm, Controller } from 'react-hook-form'
import LoxSelect from '../../../components/LoxSelect'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { selectPaymentScheduleData } from "../../../redux/paymentScheduleDataSlice"
import SpecialInstruction from '../../../components/elements/SpecialInstruction'

export default function ACHRevocationModal() {
  const dispatch = useDispatch()
  const modal = useSelector(selectModal)
  const navi = useNavigate()
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const achRevocationReason = dropdowns.data.filter(
    (o) => o.code_key === 'ach_revocation_reason'
  )[0]
  const achRevocationReasonOptions = dropdownOptions
    .filter((o) => o.dropdown_id === achRevocationReason.id)
    .map((o) => ({ label: o.name, value: o.id }))
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm()
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const loanId = modal.options?.loanId
  const paymentScheduleData = useSelector(selectPaymentScheduleData)
  const active = modal.is_active && modal.type === 'ach_revocation_modal'

  const resetState = () => {
    reset()
    setSaving(false)
  }

  // This modal stays mounted in the Footer between openings; don't carry the previous customer's
  // revocation reason into this one.
  useEffect(() => {
    if (active) {
      resetState()
    }
  }, [active, customerId])

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }))
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    try {
      let body = {}
      if (Object.keys(formData).length > 0) {
        if (paymentScheduleData.paymentScheduleCurrentDate !== null) {
          body.current_date = paymentScheduleData.paymentScheduleCurrentDate
        }
        body.ach_revocation_reason_dropdown_option_id = formData.ach_revocation_reason_dropdown_option_id.value
        body.revoke = true
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/ach_revocation/revoke`)
      } else {
        body.revoke = false
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/ach_revocation/revert`)
      }

      if (loanId) {
        body.loan_id = loanId
      }

      const response = await request.post(body)
      if (response.status === 200) {
        // reload the page
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        setTimeout(() => navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } }), 2000);
        modal.options.successCallback()
        handleCancel()
        if (typeof paymentScheduleData.refreshPaymentSchedule === 'function') { // Check that it's a function
          dispatch(paymentScheduleData.refreshPaymentSchedule)
        }
        dispatch(setNotification({ type: 'success', body: '' }))
      } else {
        modal.options.successCallback()
        setSaving(false)
        dispatch(
          setNotification({ type: 'error', body: `Error: ${response.status}` })
        )
        // there can be partial success even there is an error
        if (typeof paymentScheduleData.refreshPaymentSchedule === 'function') { // Check that it's a function
          dispatch(paymentScheduleData.refreshPaymentSchedule)
        }
      }
    } catch (error) {
      modal.options.successCallback()
      // there can be partial success even there is an error
      if (typeof paymentScheduleData.refreshPaymentSchedule === 'function') { // Check that it's a function
        dispatch(paymentScheduleData.refreshPaymentSchedule)
      }
      setSaving(false)
      const errorMessage =
        error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="ach_revocation_modal_form">
      <div className="modal ach_revocation_modal is-active" data-testid="ach-revocation-modal">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">ACH Revocation</p>
            <button
              className="delete"
              aria-label="close"
              onClick={handleCancel}
            ></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: '90px', overflow: 'hidden' }}
          >
            <div className="columns mb-0 is-flex-direction-column">
              {modal.options.hasAchRevoked ? (
                <>
                  {modal.options?.removeACHText && (!modal.options?.hasBkConfirmed) && (
                    <div className="mt-2">{modal.options.removeACHText}</div>
                  )}

                  {modal.options?.hasBkConfirmed ? (

                    <div data-testid="ach-revocation-modal-bk-notice" className="mt-5 has-text-centered has-text-danger has-text-weight-bold">This loan has an ACH revocation which cannot be removed due to an active bankruptcy</div>

                  ) : (
                    <>
                      <div className="mt-5 has-text-centered has-text-danger has-text-weight-bold">ACH Revocation
                        Active
                      </div>
                      <div>
                        Are you sure you want to remove this?<br />
                        Did the customer state they want to re-enable the ACH pulls already on the loan agreement?<br />

                        <i>NOTE: This will not automatically change any payment_methods.</i><br />
                      </div>
                    </>

                  )}

                </>
              ) : (
                <>
                  <div className="column">
                    <h3>
                      Use this action when a customer requests/demands that their ACH transactions be revoked.
                    </h3>
                  </div>
                  <div className="column">
                    <h3>Why is ACH being revoked?</h3>
                    <Controller
                      name="ach_revocation_reason_dropdown_option_id"
                      control={control}
                      rules={{ required: "Please select a reason" }}
                      render={({ field }) => (
                        <LoxSelect
                          {...field}
                          testId="ach-revocation-modal-reason"
                          options={achRevocationReasonOptions}
                          isSearchable={false}
                          classNames={reactSelectClassNames(errors, 'ach_revocation_reason_dropdown_option_id')}
                          onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                          menuPosition="fixed"
                          styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          form="ach_revocation_modal_form"
                        />
                      )}
                    />
                    {errors?.ach_revocation_reason_dropdown_option_id?.message && <p className="has-text-danger">{errors?.ach_revocation_reason_dropdown_option_id?.message}</p>}
                  </div>
                  <SpecialInstruction
                    content={"<span>" +
                      "<span class='has-text-weight-bold'>This action will perform the following actions:</span><br>" +
                      "<span class='pl-2'>• Will set ach_revocation_reason</span><br> " +
                      "<span class='pl-2'>• Will change all outstanding payments from ACH to Personal Check</span><br> " +
                      "<span class='pl-2'>• Will stop future ACH payments from being created</span><br> " +
                      "<span class='pl-2'>• Will send customer the ach_revocation_confirmation email</span><br> " +
                      " </span>"}
                    type="is-warning"
                  />
                </>
              )}
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                data-testid="ach-revocation-modal-cancel"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
              >
                Cancel
              </button>
              {modal.options.hasAchRevoked ? (
                  !modal.options?.hasBkConfirmed && (
                      <button
                        type="button"
                        data-testid="ach-revocation-modal-remove"
                        className="button is-small has-background-danger has-text-white px-2"
                        onClick={() => {
                          reset();
                          handleSubmit(onSubmit)();
                        }}
                        disabled={saving && 'disabled'}
                      >
                        {saving ? 'Removing ACH Revocation' : 'Remove ACH Revocation'}
                      </button>
                    )

              ) : (
                <button
                  type="submit"
                  data-testid="ach-revocation-modal-continue"
                  className="button is-link is-small"
                  disabled={saving && 'disabled'}
                >
                  {saving ? 'Submitting' : 'Continue'}
                </button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null
}
